import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Download, Plus, QrCode, Search, Pencil, Gift } from 'lucide-react';
import { adminEnlacesAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import DateRangeFilter, { getPresetRange } from './DateRangeFilter';

/*
  Enlaces: closers y canales en una sola tabla.

  Para quien administra, un enlace es un enlace — una dirección corta con su QR,
  un interruptor de beneficio y sus números — venga de una persona o de un QR
  pegado en el outlet (Ricardo, 16-sep-2026).

  El interruptor es lo que decide el recorrido:
    · encendido: quien entra por ese enlace ve el beneficio de bienvenida y
      puede usarlo antes de pagar;
    · apagado: entra a la tienda normal, ve lo que hay y compra.

  El embudo es el que pidió Iván: cuántos abrieron el enlace, cuántos se
  registraron, cuántos usaron el gratis y cuántos compraron.

  Los closers se dan de alta en «Equipo» —llevan contraseña, cupón y comisión—;
  aquí solo se les enciende o apaga el beneficio.
*/

const FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'closer', label: 'Closers' },
  { id: 'canal', label: 'Canales' },
];

const pct = (parte, total) => (total > 0 ? `${Math.round((parte / total) * 100)}%` : '—');

/** Copia el SVG del QR a un PNG y lo descarga, listo para imprimir. */
function descargarQR(contenedor, archivo) {
  const svg = contenedor?.querySelector('svg');
  if (!svg) return;
  const datos = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  img.onload = () => {
    const lienzo = document.createElement('canvas');
    lienzo.width = img.width;
    lienzo.height = img.height;
    const ctx = lienzo.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, lienzo.width, lienzo.height);
    ctx.drawImage(img, 0, 0);
    const a = document.createElement('a');
    a.href = lienzo.toDataURL('image/png');
    a.download = archivo;
    a.click();
  };
  img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(datos)))}`;
}

const copiar = (texto) => {
  navigator.clipboard?.writeText(texto);
  toast({ title: 'Copiado', description: texto });
};

const Numero = ({ valor, base }) => (
  <div className="text-right">
    <div className="text-gray-900 font-semibold tabular-nums">{valor}</div>
    {base !== undefined && <div className="text-xs text-gray-400 tabular-nums">{pct(valor, base)}</div>}
  </div>
);

const DialogoQR = ({ enlace, alCerrar }) => {
  const caja = useRef(null);
  if (!enlace) return null;

  return (
    <Dialog open onOpenChange={(v) => !v && alCerrar()}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{enlace.nombre}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div ref={caja} className="p-3 bg-white rounded-xl border border-gray-200">
            <QRCodeSVG value={enlace.enlace} size={240} level="H" includeMargin />
          </div>
          <p className="text-xs text-center text-gray-500">
            {enlace.da_beneficio
              ? 'Quien lo escanee verá el beneficio de bienvenida.'
              : 'Quien lo escanee entrará a la tienda normal, sin beneficio de bienvenida.'}
          </p>
          <div className="w-full flex gap-2">
            <Input value={enlace.enlace} readOnly className="bg-gray-50 border-gray-300 text-sm" />
            <button onClick={() => copiar(enlace.enlace)} className="px-3 rounded-lg border border-gray-300 hover:bg-gray-100" title="Copiar">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => descargarQR(caja.current, `vondi-${enlace.codigo}.png`)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800"
          >
            <Download className="w-4 h-4" /> Descargar para imprimir
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DialogoCanal = ({ canal, alCerrar, alGuardar }) => {
  const esNuevo = !canal?.id;
  const [forma, setForma] = useState({
    nombre: canal?.nombre ?? '',
    codigo: canal?.codigo ?? '',
    da_beneficio: canal?.da_beneficio ?? false,
    notas: canal?.notas ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const codigoCambia = !esNuevo && forma.codigo.toUpperCase() !== (canal.codigo ?? '');

  const guardar = async () => {
    if (!forma.nombre.trim()) {
      toast({ title: 'Falta el nombre', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      const datos = {
        nombre: forma.nombre.trim(),
        da_beneficio: forma.da_beneficio,
        notas: forma.notas || null,
        ...(forma.codigo.trim() && { codigo: forma.codigo.trim() }),
      };
      if (esNuevo) await adminEnlacesAPI.create(datos);
      else await adminEnlacesAPI.update('canal', canal.id, datos);
      toast({ title: esNuevo ? 'Canal creado' : 'Cambios guardados' });
      alGuardar();
    } catch (e) {
      toast({ title: 'No se pudo guardar', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && alCerrar()}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{esNuevo ? 'Nuevo canal' : `Editar ${canal.nombre}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input
              value={forma.nombre}
              onChange={(e) => setForma({ ...forma, nombre: e.target.value })}
              placeholder="Ej: Banner entrada principal"
              className="bg-gray-50 border-gray-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Código del enlace</Label>
            <div className="flex items-center rounded-md border border-gray-300 bg-gray-50 overflow-hidden">
              <span className="pl-3 text-sm text-gray-400 whitespace-nowrap">vondi.mx/r/</span>
              <input
                value={forma.codigo}
                onChange={(e) => setForma({ ...forma, codigo: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase() })}
                placeholder={esNuevo ? 'se genera solo' : ''}
                className="flex-1 bg-transparent px-1 py-2 text-sm outline-none uppercase"
              />
            </div>
            {codigoCambia && (
              <p className="text-xs text-amber-700">
                Ojo: los QR que ya se imprimieron con el código anterior dejarán de funcionar.
              </p>
            )}
          </div>
          <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Da beneficio de bienvenida</div>
              <p className="text-xs text-gray-500 mt-0.5">
                Encendido, quien entre por aquí puede usar un beneficio gratis antes de pagar.
              </p>
            </div>
            <Switch checked={forma.da_beneficio} onCheckedChange={(v) => setForma({ ...forma, da_beneficio: v })} />
          </div>
          <div className="space-y-1.5">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={forma.notas}
              onChange={(e) => setForma({ ...forma, notas: e.target.value })}
              placeholder="Dónde está pegado, qué campaña es…"
              className="bg-gray-50 border-gray-300"
              rows={2}
            />
          </div>
          <button
            onClick={guardar}
            disabled={guardando}
            className="w-full py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : esNuevo ? 'Crear canal' : 'Guardar'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Enlaces = () => {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [preset, setPreset] = useState('30');
  const [rango, setRango] = useState(() => getPresetRange('30'));
  const [qr, setQr] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cambiando, setCambiando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await adminEnlacesAPI.getAll(rango ? { start: rango.start, end: rango.end } : {});
      setFilas(r?.data ?? []);
    } catch (e) {
      toast({ title: 'No se pudieron cargar los enlaces', description: e.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const alCambiarPreset = (id) => {
    setPreset(id);
    if (id !== 'custom') setRango(getPresetRange(id));
  };

  const cambiar = async (fila, datos, mensaje) => {
    setCambiando(`${fila.tipo}-${fila.id}`);
    try {
      await adminEnlacesAPI.update(fila.tipo, fila.id, datos);
      setFilas((prev) => prev.map((f) => (f.tipo === fila.tipo && f.id === fila.id ? { ...f, ...datos } : f)));
      toast({ title: mensaje });
    } catch (e) {
      toast({ title: 'No se pudo guardar', description: e.message, variant: 'destructive' });
    } finally {
      setCambiando(null);
    }
  };

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas
      .filter((f) => filtro === 'todos' || f.tipo === filtro)
      .filter((f) => !q || f.nombre?.toLowerCase().includes(q) || f.codigo?.toLowerCase().includes(q))
      .sort((a, b) => b.pagaron - a.pagaron || b.registros - a.registros || b.visitas - a.visitas);
  }, [filas, filtro, busqueda]);

  const totales = useMemo(() => visibles.reduce(
    (t, f) => ({
      visitas: t.visitas + f.visitas,
      registros: t.registros + f.registros,
      gratis: t.gratis + f.gratis,
      pagaron: t.pagaron + f.pagaron,
    }),
    { visitas: 0, registros: 0, gratis: 0, pagaron: 0 },
  ), [visibles]);

  return (
    <div className="p-6 space-y-6">
      <Toaster />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-bold text-gray-900">Enlaces</h2>
          <p className="text-gray-500 mt-1">
            Cada closer y cada canal tiene su enlace y su QR. Con el <strong>beneficio de bienvenida</strong> encendido,
            quien entra por ahí puede usar un beneficio gratis antes de pagar; apagado, entra a la tienda normal.
            Quien entra directo a vondi.mx nunca ve el gratis.
          </p>
        </div>
        <button
          onClick={() => setEditando({})}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" /> Nuevo canal
        </button>
      </div>

      <DateRangeFilter
        preset={preset}
        start={rango?.start ?? ''}
        end={rango?.end ?? ''}
        onPresetChange={alCambiarPreset}
        onStartChange={(v) => setRango((r) => ({ ...r, start: v }))}
        onEndChange={(v) => setRango((r) => ({ ...r, end: v }))}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['Abrieron el enlace', totales.visitas],
          ['Se registraron', totales.registros],
          ['Usaron el gratis', totales.gratis],
          ['Compraron el acceso', totales.pagaron],
        ].map(([rotulo, n]) => (
          <div key={rotulo} className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500">{rotulo}</div>
            <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{n}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1">
          {FILTROS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFiltro(f.id)}
              className={`px-3 py-1.5 rounded-md text-sm ${filtro === f.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o código"
            className="pl-9 w-64 bg-white border-gray-300"
          />
        </div>
      </div>

      {cargando && filas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Cargando…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">Enlace</th>
                  <th className="px-5 py-3 text-left font-semibold">
                    <span className="inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Beneficio gratis</span>
                  </th>
                  <th className="px-5 py-3 text-right font-semibold">Abrieron</th>
                  <th className="px-5 py-3 text-right font-semibold">Registros</th>
                  <th className="px-5 py-3 text-right font-semibold">Usaron gratis</th>
                  <th className="px-5 py-3 text-right font-semibold">Compraron</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibles.map((f) => {
                  const clave = `${f.tipo}-${f.id}`;
                  return (
                    <tr key={clave} className={f.activo ? '' : 'opacity-50'}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{f.nombre}</span>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            f.tipo === 'closer' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
                          }`}>
                            {f.tipo === 'closer' ? 'Closer' : 'Canal'}
                          </span>
                          {!f.activo && <span className="text-[10px] uppercase text-gray-500">inactivo</span>}
                        </div>
                        {f.enlace ? (
                          <button
                            onClick={() => copiar(f.enlace)}
                            className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
                            title="Copiar enlace"
                          >
                            {f.enlace.replace(/^https?:\/\//, '')} <Copy className="w-3 h-3" />
                          </button>
                        ) : (
                          <div className="text-xs text-gray-400 mt-0.5">sin código</div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <Switch
                          checked={f.da_beneficio}
                          disabled={cambiando === clave}
                          onCheckedChange={(v) => cambiar(
                            f,
                            { da_beneficio: v },
                            v ? `${f.nombre} ya da el beneficio de bienvenida` : `${f.nombre} ya no da el beneficio de bienvenida`,
                          )}
                        />
                      </td>
                      <td className="px-5 py-3"><Numero valor={f.visitas} /></td>
                      <td className="px-5 py-3"><Numero valor={f.registros} base={f.visitas} /></td>
                      <td className="px-5 py-3"><Numero valor={f.gratis} base={f.registros} /></td>
                      <td className="px-5 py-3"><Numero valor={f.pagaron} base={f.registros} /></td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          {f.enlace && (
                            <button onClick={() => setQr(f)} className="p-2 rounded-lg hover:bg-gray-100" title="Ver QR">
                              <QrCode className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                          {f.tipo === 'canal' ? (
                            <>
                              <button onClick={() => setEditando(f)} className="p-2 rounded-lg hover:bg-gray-100" title="Editar">
                                <Pencil className="w-4 h-4 text-gray-600" />
                              </button>
                              <button
                                onClick={() => cambiar(f, { activo: !f.activo }, f.activo ? `${f.nombre} desactivado` : `${f.nombre} activado`)}
                                disabled={cambiando === clave}
                                className="px-2 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-100"
                              >
                                {f.activo ? 'Desactivar' : 'Activar'}
                              </button>
                            </>
                          ) : (
                            <span className="px-2 py-1 text-xs text-gray-400" title="Código, descuento y estado se cambian en Equipo">
                              en Equipo
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-gray-500">No hay enlaces que coincidan.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Los QR impresos antes con la dirección anterior (<code>?utm_source=</code>) siguen funcionando y cuentan aquí.
        «Compraron» cuenta a quien tiene al menos un pago, el día que abrió su suscripción.
      </p>

      <DialogoQR enlace={qr} alCerrar={() => setQr(null)} />
      {editando && (
        <DialogoCanal
          canal={editando}
          alCerrar={() => setEditando(null)}
          alGuardar={() => { setEditando(null); cargar(); }}
        />
      )}
    </div>
  );
};

export default Enlaces;
